/**
 * Voyage Details, Voyage Timeline, Voyage Summary, All Reports kartları ve Reference Data (Event
 * Types, Report Templates) metinleri (ADR 0082 Ek; ortağın `sefer-3.jpeg`, `sefer-6.jpeg`).
 * Ekran adları İngilizce; Türkçe ikinci dil.
 */

export const voyageRecordEn = {
  ongoing: 'Ongoing',
  states: {
    sea: 'At Sea',
    port: 'In Port',
    anchorage: 'At Anchor',
    drift: 'Drifting',
    waiting: 'Waiting',
    planned: 'Planned',
  },
  stays: { port: 'Port Stay', anchorage: 'Anchorage', drift: 'Drifting' },
  navigation: {
    sea: 'At Sea',
    port: 'At Berth',
    anchorage: 'At Anchor',
    drift: 'Drifting',
    waiting: 'At Sea',
    unknown: 'Unknown',
  },
  commercial: { on_hire: 'On Hire', off_hire: 'Off Hire', not_assigned: 'Not Assigned' },
  categories: {
    navigation: 'Navigation',
    port: 'Port Operations',
    anchorage: 'Anchorage / Drift',
    commercial: 'Commercial',
  },
  eventTypes: {
    pilot_on: 'Pilot On',
    cast_off: 'Cast Off',
    pilot_off: 'Pilot Off',
    cosp: 'COSP',
    noon_position: 'Noon Position',
    strait_entry: 'Strait Entry',
    strait_exit: 'Strait Exit',
    canal_entry: 'Canal Entry',
    canal_exit: 'Canal Exit',
    waiting_start: 'Waiting Start',
    waiting_end: 'Waiting End',
    drift_start: 'Drift Start',
    drift_end: 'Drift End',
    anchored: 'Anchored',
    anchor_aweigh: 'Anchor Aweigh',
    eosp: 'EOSP',
    nor_tendered: 'NOR Tendered',
    nor_accepted: 'NOR Accepted',
    first_line: 'First Line Ashore',
    all_fast: 'All Fast',
    laytime_commenced: 'Laytime Commenced',
    cargo_start: 'Cargo Operation Start',
    cargo_complete: 'Cargo Operation Complete',
    on_hire: 'On Hire',
    off_hire: 'Off Hire',
  },
  sourceFields: {
    reportedAt: 'Report time',
    pilotOnBoard: 'Pilot On Board',
    pilotOff: 'Pilot Off',
    letGo: 'Let Go / Cast Off',
    firstLine: 'First Line',
    allFast: 'All Fast (report time if empty)',
    nor_tenderedAt: 'NOR › Tendered',
    nor_acceptedAt: 'NOR › Accepted',
    nor_laytimeCommencedAt: 'NOR › Laytime Commenced',
  },
  columns: {
    date: 'Date / Time (UTC)',
    event: 'Event',
    location: 'Port / Location',
    report: 'Report',
    leg: 'Leg',
    from: 'From',
    to: 'To',
    start: 'Start (UTC)',
    end: 'End (UTC)',
    distance: 'Distance (NM)',
    duration: 'Duration',
    hours: 'Hours',
    avgSpeed: 'Avg. Speed (kn)',
    fuel: 'Fuel',
    total: 'Total',
    totalMt: 'Total (MT)',
    period: 'Period',
    place: 'Place',
    voyageNo: 'Voyage No.',
    departurePort: 'Departure Port',
    arrivalPort: 'Arrival Port',
    etd: 'ETD',
  },
  details: {
    title: 'Voyage Details',
    description:
      'Voyage record with its linked reports in date order, events, leg distances, fuel consumption and voyage periods.',
    record: 'Voyage Record',
    linked: 'Linked',
    linkedReports: 'Linked Reports',
    legs: 'Voyage Legs',
    periods: 'Voyage Periods',
    events: 'Events',
    fuel: 'Fuel Consumption (MT)',
    distance: 'Distance (Actual / Planned)',
    noReports: 'No reports linked to this voyage yet',
    noReportsShip: 'Add the departure report to start the voyage.',
    noReportsWeb: 'Reports are entered on board and arrive with the next synchronisation.',
    noEvents: 'No events yet: they come from the submitted reports.',
    noLegs: 'No legs yet: a leg is a sea passage between port, anchorage and drift stays.',
    noPeriods: 'No periods yet.',
  },
  summary: {
    title: 'Voyage Summary',
    description:
      'Sailing, port, anchorage and drift time, distance and consumption, derived from the submitted reports.',
    figures: 'Voyage Figures',
    sailing: 'Sailing Time',
    port: 'Port Time',
    anchorage: 'Anchorage Time',
    drift: 'Drift Time',
    waiting: 'Waiting Time',
    passage: 'incl. strait / canal {{value}}',
    passageOf: 'of which strait / canal',
    distance: 'Total Distance',
    plannedDistance: 'Planned Distance',
    avgSpeed: 'Avg. Sailing Speed',
    consumption: 'Total Consumption',
    distanceSpeed: 'Distance, Speed and Consumption',
    time: 'Time Distribution',
    fuel: 'Fuel Consumption by Period (MT)',
    noFuel: 'No consumption recorded in the reports yet.',
    reports: 'Submitted Reports',
    final: 'Final',
    provisional: 'Provisional',
    finalNote: 'Voyage completed: this is the final summary.',
    provisionalNote: 'Voyage not completed: the figures change as reports are added or corrected.',
    noDataTitle: 'No summary yet',
    noData: 'The summary starts with the first submitted report of the voyage.',
    pdf: 'Download PDF',
    derivedNote:
      'Derived from the submitted voyage reports at the time of printing; nothing is entered twice.',
  },
  timeline: {
    title: 'Voyage Timeline',
    description:
      'Events, legs and distances, port and anchorage times of the voyage on a horizontal line.',
    card: 'Voyage Timeline',
    region: 'Voyage timeline chart',
    filterLabel: 'Timeline filter',
    filters: {
      all: 'All Events',
      reports: 'Reports',
      port: 'Port Operations',
      anchorage: 'Anchorage / Drift',
      legs: 'Voyage Legs',
    },
    fit: 'Fit View',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    eta: 'ETA {{port}}',
    est: '(est.)',
    leg: 'Leg {{no}} – {{distance}}',
    stay: '{{name}} – {{duration}}',
    planned: 'Planned – {{duration}}',
    more: '+{{count}}',
    emptyTitle: 'No timeline yet',
    empty: 'The timeline starts with the first submitted report of the voyage.',
  },
  cards: {
    quickCreate: 'Quick Create Report',
    reportType: 'Report Type',
    association: 'Voyage Association',
    noVoyage: 'No Voyage Assigned',
    linkVoyage: 'Link to Existing Voyage',
    newVoyage: 'Create New Voyage',
    noVoyageHint:
      'The report is saved without a voyage and can be linked to a voyage later; the order of the vessel still applies.',
    linkHint: 'The report form opens for the selected voyage.',
    newHint: 'Opens Create New Voyage; the report form opens for the new voyage.',
    selectVoyage: 'Voyage',
    noWritable: 'No active or planned voyage',
    create: 'Create Report',
    vesselStatus: 'Vessel Status',
    navigationStatus: 'Navigation Status',
    commercialStatus: 'Commercial Status',
    lastReport: 'Last Report ({{type}})',
    lastReportNone: 'Last Report',
    lastEvent: 'Last Event ({{type}})',
    lastEventNone: 'Last Event',
    activeVoyage: 'Active Voyage',
    viewDetails: 'View Details',
    etd: 'ETD',
    eta: 'ETA',
    est: '(est.)',
    distanceTotal: 'Distance (Total)',
    completed: 'Completed',
    legOf: 'Leg {{no}}',
    remaining: '{{value}} remaining',
    noActive: 'No active voyage',
    noActiveNote: 'An active or planned voyage appears here.',
    recentEvents: 'Recent Events',
    viewAll: 'View All',
    noEvents: 'No events yet.',
    byType: 'Reports by Type',
    period: 'Period',
    lastDays: 'Last {{count}} Days',
    reports: 'Reports',
    chart: 'Reports by type chart',
    groups: {
      noon: 'Noon Report',
      port: 'Port Reports',
      arrival_departure: 'Arrival / Departure',
      anchorage_drift: 'Anchoring / Drift',
      other: 'Others',
    },
  },
  eventTypesPage: {
    title: 'Event Types',
    description:
      'Events recorded in the voyage reports, with the report and field each one comes from. Read only.',
    table: 'Event types',
    event: 'Event',
    category: 'Category',
    source: 'Source Report · Field',
    schema1: 'earlier format',
    note: 'Events are not entered separately: they are the time fields of the reports. Recent Events, the Voyage Timeline and the voyage summary read this list.',
  },
  templates: {
    title: 'Report Templates',
    description:
      'Fields of each voyage report type with their type and whether they are required. Read only; taken from the report forms.',
    reportType: 'Report Type',
    table: 'Report fields',
    count: '{{count}} fields, {{required}} required',
    field: 'Field',
    type: 'Type',
    requirement: 'Requirement',
    required: 'Required',
    optional: 'Optional',
    note: 'Daily reports ask for more fields when they are submitted. The forms themselves open from All Reports.',
    types: {
      text: 'Text',
      number: 'Number',
      time: 'Date / Time (UTC)',
      option: 'Selection',
      flag: 'Yes / No',
      block: 'Section',
      list: 'Table',
      position: 'Position',
    },
    fields: {
      reportedAt: 'Event Time (UTC)',
      tzOffsetMin: 'Time Zone',
      position: 'Position (Lat / Lon)',
      port: 'Port',
      portCode: 'Port Code',
      terminal: 'Terminal / Berth',
      nextPort: 'Next Port',
      nextPortCode: 'Next Port Code',
      nextPortEta: 'ETA Next Port',
      distanceToNextPortNm: 'Distance to Next Port',
      nextPortUnknownReason: 'Next Port Unknown – Reason',
      pilotOnBoard: 'Pilot On Board',
      pilotOff: 'Pilot Off',
      pilotNotUsed: 'Pilot Not Used',
      tugsUsed: 'Number of Tugs Used',
      tugs: 'Tugs',
      draft: 'Draft FWD / AFT',
      loadCondition: 'Load Condition',
      airDraftM: 'Air Draft',
      displacementMt: 'Displacement',
      cargo: 'Cargo & Operations',
      classification: 'Departure Classification',
      voyageAction: 'Voyage Decision',
      noVoyageReason: 'No Voyage – Reason',
      checklist: 'Departure Checklist',
      openDefects: 'Open Defects / Restrictions',
      portCall: 'Port Call',
      rob: 'ROB – Fuel',
      lubes: 'ROB – Lube Oil',
      freshWater: 'ROB – Fresh Water',
      weather: 'Weather',
      persons: 'Persons on Board',
      shipCondition: 'Ship Condition',
      remarks: 'Remarks',
      nextOperation: 'Next Operation',
      course: 'Course',
      rpm: 'RPM',
      speedKn: 'Speed',
      navigation: 'Navigation',
      machinery: 'Machinery',
      fuel: 'Fuel, Lube Oil & Fresh Water',
      distanceSinceLast: 'Distance Since Last Report',
      distanceToPortNm: 'Distance to Port',
      nor: 'NOR (Notice of Readiness)',
      firstLine: 'First Line',
      allFast: 'All Fast',
      berthSide: 'Port / Starboard Side',
      distanceToShoreM: 'Distance to Shore',
      mooring: 'Mooring',
      reason: 'Reason',
      location: 'Location Status',
      etd: 'ETD',
      etdRemarks: 'ETD Remarks',
      cargoProgress: 'Cargo / Operation Progress',
      runningHours: 'Running Hours',
      consumptionMt: 'Daily Consumption (earlier format)',
      consumption: 'Fuel Consumption (ledger)',
      name: 'Name',
      operation: 'Operation',
      charterPartyNo: 'Charter Party No',
    },
  },
};

export const voyageRecordTr: typeof voyageRecordEn = {
  ongoing: 'Sürüyor',
  states: {
    sea: 'Denizde',
    port: 'Limanda',
    anchorage: 'Demirde',
    drift: 'Sürüklenmede',
    waiting: 'Beklemede',
    planned: 'Planlanan',
  },
  stays: { port: 'Liman Kalışı', anchorage: 'Demir', drift: 'Sürüklenme' },
  navigation: {
    sea: 'Denizde',
    port: 'Rıhtımda',
    anchorage: 'Demirde',
    drift: 'Sürüklenmede',
    waiting: 'Denizde',
    unknown: 'Bilinmiyor',
  },
  commercial: { on_hire: 'Kirada', off_hire: 'Kira Dışı', not_assigned: 'Atanmamış' },
  categories: {
    navigation: 'Seyir',
    port: 'Liman İşlemleri',
    anchorage: 'Demir / Sürüklenme',
    commercial: 'Ticari',
  },
  eventTypes: {
    pilot_on: 'Pilot Gemide',
    cast_off: 'Halatlar Bırakıldı',
    pilot_off: 'Pilot Ayrıldı',
    cosp: 'COSP',
    noon_position: 'Öğle Mevkii',
    strait_entry: 'Boğaz Girişi',
    strait_exit: 'Boğaz Çıkışı',
    canal_entry: 'Kanal Girişi',
    canal_exit: 'Kanal Çıkışı',
    waiting_start: 'Bekleme Başladı',
    waiting_end: 'Bekleme Bitti',
    drift_start: 'Sürüklenme Başladı',
    drift_end: 'Sürüklenme Bitti',
    anchored: 'Demirlendi',
    anchor_aweigh: 'Demir Alındı',
    eosp: 'EOSP',
    nor_tendered: 'NOR Verildi',
    nor_accepted: 'NOR Kabul Edildi',
    first_line: 'İlk Halat Karada',
    all_fast: 'All Fast',
    laytime_commenced: 'Laytime Başladı',
    cargo_start: 'Yük İşlemi Başladı',
    cargo_complete: 'Yük İşlemi Bitti',
    on_hire: 'Kiraya Girdi',
    off_hire: 'Kiradan Çıktı',
  },
  sourceFields: {
    reportedAt: 'Rapor zamanı',
    pilotOnBoard: 'Pilot Gemide',
    pilotOff: 'Pilot Ayrıldı',
    letGo: 'Halatlar Bırakıldı (Let Go / Cast Off)',
    firstLine: 'İlk Halat',
    allFast: 'All Fast (boşsa rapor zamanı)',
    nor_tenderedAt: 'NOR › Veriliş',
    nor_acceptedAt: 'NOR › Kabul',
    nor_laytimeCommencedAt: 'NOR › Laytime Başlangıcı',
  },
  columns: {
    date: 'Tarih / Saat (UTC)',
    event: 'Olay',
    location: 'Liman / Konum',
    report: 'Rapor',
    leg: 'Bacak',
    from: 'Nereden',
    to: 'Nereye',
    start: 'Başlangıç (UTC)',
    end: 'Bitiş (UTC)',
    distance: 'Mesafe (NM)',
    duration: 'Süre',
    hours: 'Saat',
    avgSpeed: 'Ort. Hız (kn)',
    fuel: 'Yakıt',
    total: 'Toplam',
    totalMt: 'Toplam (MT)',
    period: 'Dönem',
    place: 'Yer',
    voyageNo: 'Sefer No',
    departurePort: 'Kalkış Limanı',
    arrivalPort: 'Varış Limanı',
    etd: 'ETD',
  },
  details: {
    title: 'Sefer Ayrıntıları',
    description:
      'Sefer kaydı ve tarih sırasıyla bağlı raporları, olaylar, bacak mesafeleri, yakıt tüketimi ve sefer dönemleri.',
    record: 'Sefer Kaydı',
    linked: 'Bağlı',
    linkedReports: 'Bağlı Raporlar',
    legs: 'Sefer Bacakları',
    periods: 'Sefer Dönemleri',
    events: 'Olaylar',
    fuel: 'Yakıt Tüketimi (MT)',
    distance: 'Mesafe (Gerçek / Planlanan)',
    noReports: 'Bu sefere henüz bağlı rapor yok',
    noReportsShip: 'Seferi başlatmak için kalkış raporunu girin.',
    noReportsWeb: 'Raporlar gemide girilir ve bir sonraki senkronla gelir.',
    noEvents: 'Henüz olay yok: olaylar gönderilmiş raporlardan gelir.',
    noLegs:
      'Henüz bacak yok: bacak, liman, demir ve sürüklenme kalışları arasındaki deniz seyridir.',
    noPeriods: 'Henüz dönem yok.',
  },
  summary: {
    title: 'Sefer Özeti',
    description:
      'Gönderilmiş raporlardan türetilen seyir, liman, demir ve sürüklenme süresi, mesafe ve tüketim.',
    figures: 'Sefer Değerleri',
    sailing: 'Seyir Süresi',
    port: 'Liman Süresi',
    anchorage: 'Demir Süresi',
    drift: 'Sürüklenme Süresi',
    waiting: 'Bekleme Süresi',
    passage: 'boğaz / kanal dahil {{value}}',
    passageOf: 'bunun boğaz / kanal kısmı',
    distance: 'Toplam Mesafe',
    plannedDistance: 'Planlanan Mesafe',
    avgSpeed: 'Ort. Seyir Hızı',
    consumption: 'Toplam Tüketim',
    distanceSpeed: 'Mesafe, Hız ve Tüketim',
    time: 'Süre Dağılımı',
    fuel: 'Döneme Göre Yakıt Tüketimi (MT)',
    noFuel: 'Raporlarda henüz tüketim yok.',
    reports: 'Gönderilmiş Raporlar',
    final: 'Nihai',
    provisional: 'Geçici',
    finalNote: 'Sefer tamamlandı: bu nihai özettir.',
    provisionalNote: 'Sefer sürüyor: rapor eklendikçe ya da düzeltildikçe değerler değişir.',
    noDataTitle: 'Henüz özet yok',
    noData: 'Özet, seferin ilk gönderilmiş raporuyla başlar.',
    pdf: 'PDF İndir',
    derivedNote:
      'Basım anında gönderilmiş sefer raporlarından türetildi; hiçbir değer iki kez girilmez.',
  },
  timeline: {
    title: 'Sefer Zaman Çizelgesi',
    description:
      'Seferin olayları, bacakları ve mesafeleri, liman ve demir süreleri yatay bir çizgide.',
    card: 'Sefer Zaman Çizelgesi',
    region: 'Sefer zaman çizelgesi grafiği',
    filterLabel: 'Çizelge süzgeci',
    filters: {
      all: 'Tüm Olaylar',
      reports: 'Raporlar',
      port: 'Liman İşlemleri',
      anchorage: 'Demir / Sürüklenme',
      legs: 'Sefer Bacakları',
    },
    fit: 'Sığdır',
    fullscreen: 'Tam ekran',
    exitFullscreen: 'Tam ekrandan çık',
    eta: 'ETA {{port}}',
    est: '(tahmini)',
    leg: 'Bacak {{no}} – {{distance}}',
    stay: '{{name}} – {{duration}}',
    planned: 'Planlanan – {{duration}}',
    more: '+{{count}}',
    emptyTitle: 'Henüz çizelge yok',
    empty: 'Çizelge, seferin ilk gönderilmiş raporuyla başlar.',
  },
  cards: {
    quickCreate: 'Hızlı Rapor',
    reportType: 'Rapor Türü',
    association: 'Sefer Bağı',
    noVoyage: 'Sefersiz',
    linkVoyage: 'Mevcut Sefere Bağla',
    newVoyage: 'Yeni Sefer Oluştur',
    noVoyageHint:
      'Rapor sefersiz kaydedilir ve sonra bir sefere bağlanabilir; geminin rapor sırası yine geçerlidir.',
    linkHint: 'Rapor formu seçilen sefer için açılır.',
    newHint: 'Yeni Sefer formunu açar; rapor formu yeni seferle açılır.',
    selectVoyage: 'Sefer',
    noWritable: 'Süren ya da planlanan sefer yok',
    create: 'Rapor Oluştur',
    vesselStatus: 'Geminin Konumu ve Seyri',
    navigationStatus: 'Seyir Durumu',
    commercialStatus: 'Ticari Durum',
    lastReport: 'Son Rapor ({{type}})',
    lastReportNone: 'Son Rapor',
    lastEvent: 'Son Olay ({{type}})',
    lastEventNone: 'Son Olay',
    activeVoyage: 'Etkin Sefer',
    viewDetails: 'Ayrıntılar',
    etd: 'ETD',
    eta: 'ETA',
    est: '(tahmini)',
    distanceTotal: 'Mesafe (Toplam)',
    completed: 'Tamamlanan',
    legOf: 'Bacak {{no}}',
    remaining: '{{value}} kaldı',
    noActive: 'Etkin sefer yok',
    noActiveNote: 'Süren ya da planlanan sefer burada görünür.',
    recentEvents: 'Son Olaylar',
    viewAll: 'Tümü',
    noEvents: 'Henüz olay yok.',
    byType: 'Türe Göre Raporlar',
    period: 'Dönem',
    lastDays: 'Son {{count}} Gün',
    reports: 'Rapor',
    chart: 'Türe göre raporlar grafiği',
    groups: {
      noon: 'Öğle Raporu',
      port: 'Liman Raporları',
      arrival_departure: 'Varış / Kalkış',
      anchorage_drift: 'Demir / Sürüklenme',
      other: 'Diğer',
    },
  },
  eventTypesPage: {
    title: 'Olay Türleri',
    description:
      'Sefer raporlarında kaydedilen olaylar, her birinin geldiği rapor ve alanla. Salt okunur.',
    table: 'Olay türleri',
    event: 'Olay',
    category: 'Kategori',
    source: 'Kaynak Rapor · Alan',
    schema1: 'eski biçim',
    note: 'Olaylar ayrıca girilmez: raporların zaman alanlarıdır. Son Olaylar, Sefer Zaman Çizelgesi ve sefer özeti bu listeyi okur.',
  },
  templates: {
    title: 'Rapor Şablonları',
    description:
      'Her sefer rapor türünün alanları, türleri ve zorunlu olup olmadıkları. Salt okunur; rapor formlarından alınır.',
    reportType: 'Rapor Türü',
    table: 'Rapor alanları',
    count: '{{count}} alan, {{required}} zorunlu',
    field: 'Alan',
    type: 'Tür',
    requirement: 'Zorunluluk',
    required: 'Zorunlu',
    optional: 'İsteğe bağlı',
    note: 'Günlük raporlar gönderilirken daha çok alan ister. Formların kendisi Tüm Raporlar’dan açılır.',
    types: {
      text: 'Metin',
      number: 'Sayı',
      time: 'Tarih / Saat (UTC)',
      option: 'Seçim',
      flag: 'Evet / Hayır',
      block: 'Bölüm',
      list: 'Tablo',
      position: 'Mevki',
    },
    fields: {
      reportedAt: 'Olay Zamanı (UTC)',
      tzOffsetMin: 'Saat Dilimi',
      position: 'Mevki (Enlem / Boylam)',
      port: 'Liman',
      portCode: 'Liman Kodu',
      terminal: 'Terminal / Rıhtım',
      nextPort: 'Sonraki Liman',
      nextPortCode: 'Sonraki Liman Kodu',
      nextPortEta: 'Sonraki Limana ETA',
      distanceToNextPortNm: 'Sonraki Limana Mesafe',
      nextPortUnknownReason: 'Sonraki Liman Bilinmiyor – Gerekçe',
      pilotOnBoard: 'Pilot Gemide',
      pilotOff: 'Pilot Ayrıldı',
      pilotNotUsed: 'Pilot Kullanılmadı',
      tugsUsed: 'Kullanılan Römorkör Sayısı',
      tugs: 'Römorkörler',
      draft: 'Draft Baş / Kıç',
      loadCondition: 'Yük Durumu',
      airDraftM: 'Hava Draftı',
      displacementMt: 'Deplasman',
      cargo: 'Yük ve İşlemler',
      classification: 'Kalkış Sınıflandırması',
      voyageAction: 'Sefer Kararı',
      noVoyageReason: 'Sefersiz – Gerekçe',
      checklist: 'Kalkış Kontrol Listesi',
      openDefects: 'Açık Arızalar / Kısıtlar',
      portCall: 'Liman Araması',
      rob: 'ROB – Yakıt',
      lubes: 'ROB – Yağ',
      freshWater: 'ROB – Tatlı Su',
      weather: 'Hava',
      persons: 'Gemideki Kişiler',
      shipCondition: 'Gemi Durumu',
      remarks: 'Açıklama',
      nextOperation: 'Sonraki İşlem',
      course: 'Rota',
      rpm: 'Devir (RPM)',
      speedKn: 'Hız',
      navigation: 'Seyir',
      machinery: 'Makine',
      fuel: 'Yakıt, Yağ ve Tatlı Su',
      distanceSinceLast: 'Son Rapordan Beri Mesafe',
      distanceToPortNm: 'Limana Mesafe',
      nor: 'NOR (Hazırlık Bildirimi)',
      firstLine: 'İlk Halat',
      allFast: 'All Fast',
      berthSide: 'İskele / Sancak Tarafı',
      distanceToShoreM: 'Kıyıya Mesafe',
      mooring: 'Halatlar',
      reason: 'Gerekçe',
      location: 'Konum Durumu',
      etd: 'ETD',
      etdRemarks: 'ETD Açıklaması',
      cargoProgress: 'Yük / İşlem İlerlemesi',
      runningHours: 'Çalışma Saatleri',
      consumptionMt: 'Günlük Tüketim (eski biçim)',
      consumption: 'Yakıt Tüketimi (defter)',
      name: 'Ad',
      operation: 'İşlem',
      charterPartyNo: 'Çarter Sözleşmesi No',
    },
  },
};
